import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import BrandMark from '../components/BrandMark';
import AssistantWidget from '../components/AssistantWidget';
import './DashboardLayout.css';

function SidebarIcon({ name, size = 20 }) {
  const icons = {
    dashboard: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
    whatsapp: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
        <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
      </svg>
    ),
    ai: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z" />
        <path d="M16 14H8a4 4 0 0 0-4 4v2h16v-2a4 4 0 0 0-4-4z" />
        <circle cx="9" cy="7" r="1" />
        <circle cx="15" cy="7" r="1" />
      </svg>
    ),
    chat: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
    token: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v12" />
        <path d="M8 10h8" />
        <path d="M8 14h8" />
      </svg>
    ),
    calendar: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    ),
  };
  return icons[name] || null;
}

const MENU_ITEMS = [
  { path: '/app', icon: 'dashboard', key: 'panel', end: true },
  { path: '/app/whatsapp', icon: 'whatsapp', key: 'whatsapp' },
  { path: '/app/conversations', icon: 'chat', key: 'conversations' },
  { path: '/app/appointments', icon: 'calendar', key: 'appointments' },
  { path: '/app/ai-config', icon: 'ai', key: 'ai_config' },
  { path: '/app/tokens', icon: 'token', key: 'plans' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { lang, toggleLang, t } = useLang();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const planName = () => {
    try {
      const plan = typeof user?.plan === 'string' ? JSON.parse(user.plan) : user?.plan;
      return plan?.title || plan?.name || 'Free';
    } catch {
      return 'Free';
    }
  };

  return (
    <div className="dash">
      {/* Mobile overlay */}
      {mobileOpen && <div className="dash__overlay" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${mobileOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar__top">
          <div className="sidebar__logo" onClick={() => navigate('/app')}>
            <BrandMark
              imgClassName="sidebar__logo-icon sidebar__logo-icon--mark"
              fallbackClassName="sidebar__logo-icon"
            />
            {!collapsed && <span>ReplyTek</span>}
          </div>
          <button type="button" className="sidebar__toggle" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? '→' : '←'}
          </button>
        </div>

        <nav className="sidebar__nav">
          {MENU_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <span className="sidebar__link-icon">
                <SidebarIcon name={item.icon} />
              </span>
              {!collapsed && <span className="sidebar__link-text">{t(item.key)}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__bottom">
          {!collapsed && (
            <div
              className="sidebar__user"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/app/profile')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/app/profile');
                }
              }}
            >
              <div className="sidebar__avatar">{user?.name?.[0]?.toUpperCase() || 'U'}</div>
              <div className="sidebar__user-info">
                <span className="sidebar__user-name">{user?.name || 'User'}</span>
                <span className="sidebar__user-plan">{planName()}</span>
              </div>
            </div>
          )}
          <button type="button" className="sidebar__logout" onClick={handleLogout} title={t('logout')}>
            <span className="sidebar__logout-label">{t('logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className={`dash__main ${collapsed ? 'dash__main--expanded' : ''}`}>
        {/* Top bar */}
        <header className="topbar">
          <button type="button" className="topbar__hamburger" onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
          <div className="topbar__left">
            <span className="topbar__plan-badge">{planName()}</span>
            <button type="button" className="topbar__upgrade" onClick={() => navigate('/app/tokens')}>{t('upgrade')}</button>
          </div>
          <div className="topbar__right">
            <button type="button" className="topbar__lang" onClick={toggleLang}>{lang === 'en' ? 'ES' : 'EN'}</button>
          </div>
        </header>

        {/* Page content */}
        <main className="dash__content">
          <Outlet />
        </main>
      </div>

      <AssistantWidget />
    </div>
  );
}
